import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Assignment_Operators() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🧮 JavaScript Assignment Operators</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Assignment operators are used to assign values to variables. The most
        common one is the equal sign (<code>=</code>), but JavaScript provides
        many shorthand assignment operators that combine arithmetic and
        assignment.
      </p>

      {/* Key Points Table */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1.1rem",
            backgroundColor: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <thead style={{ backgroundColor: "#e2e8f0" }}>
            <tr>
              <th style={{ padding: "12px", textAlign: "left" }}>Operator</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Example</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Same As</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "10px" }}>=</td>
              <td style={{ padding: "10px" }}>x = y</td>
              <td style={{ padding: "10px" }}>x = y</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>+=</td>
              <td style={{ padding: "10px" }}>x += y</td>
              <td style={{ padding: "10px" }}>x = x + y</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>-=</td>
              <td style={{ padding: "10px" }}>x -= y</td>
              <td style={{ padding: "10px" }}>x = x - y</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>*=</td>
              <td style={{ padding: "10px" }}>x *= y</td>
              <td style={{ padding: "10px" }}>x = x * y</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>/=</td>
              <td style={{ padding: "10px" }}>x /= y</td>
              <td style={{ padding: "10px" }}>x = x / y</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>%=</td>
              <td style={{ padding: "10px" }}>x %= y</td>
              <td style={{ padding: "10px" }}>x = x % y</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>**=</td>
              <td style={{ padding: "10px" }}>x **= y</td>
              <td style={{ padding: "10px" }}>x = x ** y</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Example 1: Basic Assignment */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Assignment</h2>
        <p>
          The <code>=</code> operator assigns the value on the right to the
          variable on the left.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 10;
console.log("Value of x:", x);`}
          />
          <TryItButton
            code={`let x = 10;
console.log("Value of x:", x);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          Output: <b>Value of x: 10</b>
        </div>
      </div>

      {/* Example 2: Add and Assign (+=) */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Add and Assign (+=)</h2>
        <p>
          The <code>+=</code> operator adds the right operand to the left
          operand and assigns the result to the left operand.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 5;
x += 3;
console.log("Result:", x);`}
          />
          <TryItButton
            code={`let x = 5;
x += 3;
console.log("Result:", x);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>Result: 8</b>
        </div>
      </div>

      {/* Example 3: Subtract and Assign (-=) */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Subtract and Assign (-=)</h2>
        <p>
          The <code>-=</code> operator subtracts and assigns the result to the
          same variable.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 10;
x -= 4;
console.log("Result:", x);`}
          />
          <TryItButton
            code={`let x = 10;
x -= 4;
console.log("Result:", x);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output: <b>Result: 6</b>
        </div>
      </div>

      {/* Example 4: Multiply and Assign (*=) */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Multiply and Assign (*=)</h2>
        <p>
          The <code>*=</code> operator multiplies and assigns the result.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 4;
x *= 3;
console.log("Result:", x);`}
          />
          <TryItButton
            code={`let x = 4;
x *= 3;
console.log("Result:", x);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: <b>Result: 12</b>
        </div>
      </div>

      {/* Example 5: Divide, Modulus, Exponentiation */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Divide, Modulus, and Power</h2>
        <p>
          JavaScript also supports <code>/=</code>, <code>%=</code>, and{" "}
          <code>**=</code> for division, modulus, and exponentiation
          respectively.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 10;
x /= 2;
console.log("After Division:", x);
x %= 3;
console.log("After Modulus:", x);
x **= 3;
console.log("After Exponentiation:", x);`}
          />
          <TryItButton
            code={`let x = 10;
x /= 2;
console.log("After Division:", x);
x %= 3;
console.log("After Modulus:", x);
x **= 3;
console.log("After Exponentiation:", x);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            After Division: 5
            <br />
            After Modulus: 2
            <br />
            After Exponentiation: 8
          </pre>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Comparison Operators</b> to compare values.
          </li>
          <li>
            Explore <b>Logical Operators</b> to combine conditions.
          </li>
          <li>
            Practice building programs using multiple assignment operations.
          </li>
        </ul>
      </div>
    </div>
  );
}
